import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

interface PaginationMetadata {
    total?: number;
    totalPages?: number;
}

interface PaginationData {
    pagination?: PaginationMetadata;
    itemCount: number;
}

interface UsePaginationParams {
    defaultPageSize: number;
}

interface UsePaginationResult {
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    setPaginationData: Dispatch<SetStateAction<PaginationData>>;
    resetPage: () => void;
    handlePageSizeChange: (nextPageSize: number) => void;
}

export function usePagination({ defaultPageSize }: UsePaginationParams): UsePaginationResult {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [paginationData, setPaginationData] = useState<PaginationData>({ itemCount: 0 });

    const totalCount = paginationData.pagination?.total ?? paginationData.itemCount;
    const totalPages = Math.max(1, paginationData.pagination?.totalPages ?? Math.ceil(totalCount / pageSize));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const resetPage = () => {
        setPage(1);
    };

    const handlePageSizeChange = (nextPageSize: number) => {
        setPageSize(nextPageSize);
        resetPage();
    };

    return {
        page,
        setPage,
        pageSize,
        totalCount,
        totalPages,
        setPaginationData,
        resetPage,
        handlePageSizeChange,
    };
}
